import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthUser } from '@app/account/account-api.model';
import { InputsService } from '@app/inputs/services/inputs.service';
import { ProfileOverviewService } from '@app/profile/components/profile-overview/profile-overview.service';
import { Profile } from '@app/profile/profile.model';
import {
  CollectionCounts,
  SignalSummary,
  SignalSummaryMap,
  TagSignalCount,
  TagSignalHistory,
} from '@app/profile/profile-api.model';
import { ProfileService } from '@app/profile/services/profile.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { EngagedLearnerService } from '@app/shared/services/engaged-learner.service';
import { FollowingService } from '@app/shared/services/following.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TrackingEventArgs } from '@app/shared/services/tracking.model';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { sortBy } from '@app/shared/utils';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { NotifierService, WindowLayoutService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ObservableInput, Subscription, forkJoin, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ProfileSkillSignalServiceV2 } from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signal-page.service';

interface LoadingStates {
  page: boolean;
  learnings: boolean;
  experiences?: boolean;
  achievements?: boolean;
}

@Component({
  selector: 'dgx-profile-overview',
  templateUrl: './profile-overview.component.html',
  styleUrls: ['./profile-overview.component.scss'],
})
export class ProfileOverviewComponent
  extends SubscriberBaseDirective
  implements OnInit, OnDestroy
{
  public readonly CLASS_NAME = 'ProfileOverviewComponent';

  public i18n = this.translate.instant([
    'Core_Empty_Image_Alt',
    'dgProfileOverview_OverviewEmptyStateTitle',
    'dgProfileOverview_OverviewEmptyStateText',
    'dgProfileOverview_OverviewCompleteWizardButton',
    'dgProfileOverview_OverviewSkillsTitle',
    'dgProfileOverview_OverviewExperienceTitle',
    'dgProfileOverview_OverviewAchievementsTitle',
    'dgProfileOverview_OverviewLearningTitle',
    'dgProfileOverview_OverviewNotProvidedTitle',
    'dgProfileOverview_NonOwnerEmptyStateTitle',
  ]);

  @Input() public owner: Profile;
  @Input() public isEngaged: boolean;
  @Input() public privacyId: number;

  // Local
  public isLoading: boolean = true;
  public hasNoSkills: boolean = false;
  public isLoggedIn: boolean;
  public hasParsedResume: boolean;

  public xAxisLabels: string[];
  public signalHistories: TagSignalHistory[];
  public signalCounts: TagSignalCount[];
  public maxSkills: number = 6;
  public signalSummaries: SignalSummaryMap;

  public skills: TagsApi.FocusableTagDetails = { focus: [], other: [] };

  public collectionCounts: CollectionCounts = {
    learningsCount: 0,
    achievementsCount: 0,
    experiencesCount: 0,
  };
  public skillRatingsCount: number = 0;
  public authUser: AuthUser;
  public loadingStates: LoadingStates = {
    page: true,
    learnings: true,
  };

  public showExperiences: boolean;
  public showAchievements: boolean;

  public ownerKey: number;
  public isMobile = this.windowLayoutService.isMobile;

  // Skill suggestions
  public checkForCanonicalTags: boolean = false;
  public isSaving: boolean = false;
  public tags: TagsApi.Tag[] = [];
  public suggestedTags: TagsApi.Tag[] = [];
  public isSuggestedTagsLoading: boolean = false;
  public maxSuggestions = 8;
  public numDefaultInputTagsForSuggestions = 20;
  public currentSuggestedTagIds: number[] = [];
  // the last tag selected via the dropdown, used to generate a new set suggested tags based on this tag
  public currentTag: TagsApi.Tag;

  private events: Subscription[] = [];

  public get ownerIsViewing() {
    return this.profileService.ownerIsViewing;
  }

  constructor(
    private tagsService: TagsService,
    private translate: TranslateService,
    private tracker: TrackerService,
    private webEnvironmentService: WebEnvironmentService,
    private profileOverviewServiceV2: ProfileOverviewService,
    private authService: AuthService,
    private inputsService: InputsService,
    private engagedLearnerService: EngagedLearnerService,
    private followingService: FollowingService,
    private profileService: ProfileService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private notifierService: NotifierService,
    private profileSkillSignalsService: ProfileSkillSignalServiceV2,
    private windowLayoutService: WindowLayoutService
  ) {
    super();
  }

  public get emptyImageUrl(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/emptystate/profile-overview-empty-state.svg',
      true
    );
  }

  public get nonOwnerEmptyStateText(): string {
    return this.translate.instant('dgProfileOverview_NonOwnerEmptyStateBody', {
      firstName: this.owner.name.split(' ')[0],
    });
  }

  public get nonOwnerEmptyImageUrl(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/emptystate/profile-settings.svg',
      true
    );
  }

  public ngOnInit(): void {
    // Update skill counts when user tags are updated
    this.events.push(
      this.tagsService.userTagsModified.subscribe(() => {
        this.updateSkillsCounts();
      }),
      this.inputsService.inputModify.subscribe((...args) => {
        this.updateSkillsCounts();
      })
    );

    this.checkForCanonicalTags =
      this.authService.authUser?.defaultOrgInfo?.settings.enableWorkdaySkillsIntegration;

    this.authUser = this.authService?.authUser;

    this.showExperiences = !this.authUser?.hideOverviewExperience;
    this.showAchievements = !this.authUser?.hideOverviewAchievement;
    this.setupLoadingStates();

    this.isLoggedIn = !!this.authUser;
    this.ownerKey = this.owner.id;

    if (this.isLoggedIn) {
      this.getTagSuggestions();
    }

    forkJoin({
      hasParsedResume: this.ownerIsViewing
        ? this.profileOverviewServiceV2.getHasParsedResume()
        : of({ hasBeenParsed: false }),
      collectionCounts: this.profileSkillSignalsService.getCollectionCounts(
        this.ownerKey,
        this.isLoggedIn
      ),
      skillsAndRatingsCount: this.profileSkillSignalsService.getUserSkills(
        this.ownerKey
      ),
      signalCounts: this.profileSkillSignalsService.getSignalCounts(
        this.ownerKey
      ),
    })
      .pipe(
        take(1),
        switchMap(
          ({
            hasParsedResume,
            collectionCounts,
            skillsAndRatingsCount,
            signalCounts,
          }): ObservableInput<any> => {
            this.hasParsedResume = hasParsedResume.hasBeenParsed;
            this.collectionCounts = collectionCounts;
            const { skills, ratingsTotalCount } = skillsAndRatingsCount;
            this.skillRatingsCount = ratingsTotalCount;
            this.setSkills(skills);
            this.signalCounts = signalCounts.signalCounts;
            this.signalHistories = signalCounts.signalHistories;
            this.xAxisLabels = signalCounts.xAxisLabels;

            if (this.hasSkills(skills)) {
              const focusTagIds = skills.focus.map((s) => s.tagId);
              const otherTagIds = skills.other
                .slice(0, 6)
                .reduce((allSkills, skill) => {
                  if (!focusTagIds.includes(skill.tagId)) {
                    allSkills.push(skill.tagId);
                  }
                  return allSkills;
                }, []);

              const allTagIds = [...focusTagIds, ...otherTagIds].join(',');

              return this.profileSkillSignalsService.getSignalBreakdown(
                this.ownerKey,
                allTagIds
              );
            }
            this.updateLoadingState({ type: 'page', status: false });
            return of([]);
          }
        )
      )
      .subscribe((signalSummary) => {
        if (signalSummary) {
          this.signalSummaries = signalSummary.reduce((all, current) => {
            all[current.tagId] = current;
            return all;
          }, {});
        }
        this.trackProfileView();
        this.updateLoadingState({ type: 'page', status: false });
      });

    this.windowLayoutService.windowResize$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.isMobile = this.windowLayoutService.isMobile;
      });
  }

  public ngOnDestroy(): void {
    this.events.forEach((sub) => sub.unsubscribe());
  }

  public setupLoadingStates(): void {
    this.loadingStates = { page: true, learnings: true };
    // don't add experiences or achievements if they aren't to be shown
    if (this.showExperiences) {
      this.loadingStates.experiences = true;
    }
    if (this.showAchievements) {
      this.loadingStates.achievements = true;
    }
  }

  public updateLoadingState({
    type,
    status,
  }: {
    type: string;
    status: boolean;
  }) {
    // Collect loading states from sub-components and this page, isLoading is changed to true when at least one of the
    // states is true and changed to false when all states have changed to false
    this.loadingStates[type] = status;
    this.isLoading = !!Object.values(this.loadingStates).find((v) => !!v);
  }

  public updateSkillsCounts(): void {
    const isLoggedIn = !!this.authUser;

    forkJoin({
      collectionCounts: this.profileSkillSignalsService.getCollectionCounts(
        this.ownerKey,
        isLoggedIn
      ),
      skillsAndRatingsCount: this.profileSkillSignalsService.getUserSkills(
        this.ownerKey
      ),
      signalCounts: this.profileSkillSignalsService.getSignalCounts(
        this.ownerKey
      ),
    })
      .pipe(
        take(1),
        switchMap(
          ({
            collectionCounts,
            skillsAndRatingsCount,
            signalCounts,
          }): Observable<SignalSummary[]> => {
            this.collectionCounts = collectionCounts;
            const { skills, ratingsTotalCount } = skillsAndRatingsCount;
            this.skillRatingsCount = ratingsTotalCount;
            this.setSkills(skills);
            this.signalCounts = signalCounts.signalCounts;
            this.signalHistories = signalCounts.signalHistories;
            this.xAxisLabels = signalCounts.xAxisLabels;

            const allTagIds = skills.other.slice(0, 6).concat(skills.focus);

            // get the unique tag ids from the focus skills and the top 6 other skills so that we only get the signal
            // summaries for these tags
            const uniqueTagIds = allTagIds
              .reduce((all, current) => {
                if (!all.includes(current.tagId)) {
                  all.push(current.tagId);
                }
                return all;
              }, [])
              .join(',');

            return this.profileSkillSignalsService.getSignalBreakdown(
              this.ownerKey,
              uniqueTagIds
            );
          }
        )
      )
      .subscribe((signalSummary) => {
        this.signalSummaries = signalSummary.reduce((all, current) => {
          all[current.tagId] = current;
          return all;
        }, {});
        this.updateLoadingState({ type: 'page', status: false });
      });
  }

  public setSkills(skills: TagsApi.FocusableTagDetails): void {
    if (this.hasSkills(skills)) {
      this.skills = skills;
      this.hasNoSkills = !skills?.focus.length && !skills?.other.length;
    } else {
      this.hasNoSkills = true;
      this.forceLoadingStatus(false);
    }
  }

  public goToWizard(): void {
    this.router.navigateByUrl('/getstarted/welcome');
  }

  /**
   * Get the set of suggested tags. If this.currentTag has been set then this tag is used as the input for the suggestions.
   * this.currentTag is set when a user has a selected a tag from the dgx-tags-edit component and not from the suggested tags list.
   * If this.currentTag has not been set - eg, when the modal is first displayed, or the corresponding tag has been
   * removed - then the top 20 tags from the user's tag list are used, based on the latest dateModified values.
   */
  public getTagSuggestions(): void {
    this.isSuggestedTagsLoading = true;
    this.suggestedTags = [];

    const userTags = this.authService.authUser.viewerInterests;

    let topUserTags;

    if (this.currentTag) {
      topUserTags = this.currentTag.name;
    } else {
      // Get the top 20 tags based on dateModified, sorted in descending order if currentTag is not supplied
      topUserTags = userTags
        .sort((a, b) => sortBy(b, a, 'dateModified'))
        .slice(0, this.numDefaultInputTagsForSuggestions)
        .map((t) => t.name)
        .join(',');
    }

    const allTagIds = userTags.map((t) => t.tagId);
    const allTagNames = userTags.map((t) => t.name.toLowerCase());
    this.tagsService
      .getTagSuggestions(topUserTags, this.maxSuggestions)
      .pipe(take(1))
      .subscribe(({ tags, isFromDs3 }) => {
        this.suggestedTags = tags.reduce((allTags, tag) => {
          if (
            !allTagIds.includes(tag.tagId) &&
            !allTagNames.includes(tag.name.toLowerCase())
          ) {
            allTags.push(tag);
          }
          return allTags;
        }, []);
        this.currentSuggestedTagIds = this.suggestedTags.map((t) => t.tagId);
        this.isSuggestedTagsLoading = false;
        this.cdr.detectChanges();
      });
  }

  /**
   * Add a tag from the suggested tags list
   * @param tag - the TagsApi.Tag object that was added from the suggested tags list
   */
  public addSuggestedTag(tag: TagsApi.Tag): void {
    // Prevent duplicate tag items from being added, this can only really happen if someone types in the name of a
    // tag that is also in the suggested tags list, selects it and then tries to add the tag from the suggestions as well
    const isFound = (item: TagsApi.Tag) =>
      item.title.trim().toLowerCase() === tag.title.trim().toLowerCase() ||
      item.name.trim().toLowerCase() === tag.name.trim().toLowerCase();
    const existingTagIndex = this.tags.findIndex(isFound);
    if (existingTagIndex !== -1) {
      // Remove the existing tag
      this.remove(tag);
    } else {
      // Add the new tag
      this.tags = [...this.tags, tag];
      this.save();
    }
  }

  /**
   * Check the selected tags to see if they have canonical workday skills before saving.
   */
  public save() {
    if (this.tags.length === 0) {
      return;
    }

    this.isSaving = true;

    if (this.checkForCanonicalTags) {
      this.saveCanonicalTags();
    } else {
      this.saveTags();
    }
  }

  /**
   * Save the selected tags via the tagsService
   */
  public saveTags(): void {
    this.tagsService.addUserTags(this.tags).subscribe(() => {
      this.isSaving = false;
      this.authService.fetchAuthenticatedUser().subscribe();
      this.notifierService.showSuccess(
        this.translate.instant('dgProfileOverview_AddSkill', {
          skillName: this.tags[this.tags.length - 1].title,
        })
      );
    });
  }

  /**
   *  Look for tags with canonical alternatives and save any that do not
   */
  public saveCanonicalTags(): void {
    this.tagsService.getAllCanonicalTags(this.tags).subscribe((results) => {
      const sorted = this.tags.reduce(
        (acc, curr) => {
          const canonical = results.find((result) => result.tag === curr.name);
          if (
            canonical?.hasCanonical &&
            canonical?.tag !== canonical?.canonicalTag
          ) {
            acc.canonical.push(canonical);
          } else {
            acc.nonCanonical.push(curr);
          }
          return acc;
        },
        { canonical: [], nonCanonical: [] }
      );

      if (sorted.nonCanonical.length) {
        this.tagsService.addUserTags(sorted.nonCanonical).subscribe(() => {
          this.isSaving = false;
          this.authService.fetchAuthenticatedUser().subscribe();
          this.notifierService.showSuccess(
            this.translate.instant('dgProfileOverview_AddSkill', {
              skillName: this.tags[this.tags.length - 1].title,
            })
          );
        });
      } else {
        this.isSaving = false;
        this.authService.fetchAuthenticatedUser().subscribe();
      }
    });
  }

  public remove(tag: TagsApi.Tag) {
    this.isSaving = true;
    this.removeTag(tag);
  }

  public removeTag(tag: TagsApi.Tag): void {
    this.tagsService.removeUserTag(tag).subscribe(() => {
      this.isSaving = false;
      this.authService.fetchAuthenticatedUser().subscribe();
      this.tags = this.tags.filter(
        (t) =>
          t.title.trim().toLowerCase() !== tag.title.trim().toLowerCase() ||
          t.name.trim().toLowerCase() !== tag.name.trim().toLowerCase()
      );
    });
  }

  private forceLoadingStatus(status: boolean): void {
    for (const key of Object.keys(this.loadingStates)) {
      this.loadingStates[key] = status;
    }
    this.isLoading = status;
  }

  private hasSkills(skills: TagsApi.FocusableTagDetails): boolean {
    return skills?.focus?.length > 0 || skills?.other?.length > 0;
  }

  private trackProfileView(): void {
    const profileOwnerId = this.ownerIsViewing ? 'self' : this.owner.id;
    const privacyOptions = { 0: 'Public', 1: 'OrgVisible', 2: 'Private' };
    const isFollowing = this.ownerIsViewing
      ? 'self'
      : this.followingService.isViewersFollowee(this.owner.guid);
    // @TODO: Update to new angular when it's ready - PD-52198
    const isEngaged = this.engagedLearnerService.isEnabled && this.isEngaged;
    const trackProperties = {
      ProfileOwnerId: profileOwnerId,
      ProfileVisibility: privacyOptions[this.privacyId],
      SkillsAddedCount: this.skills.other.length,
      SkillRatingsCount: this.skillRatingsCount,
      FocusSkillsCount: this.skills.focus.length,
      ExperiencesCount: this.collectionCounts.experiencesCount,
      AchievementsCount: this.collectionCounts.achievementsCount,
      LearningsCount: this.collectionCounts.learningsCount,
      IsActiveLearner: isEngaged,
      IsFollowing: isFollowing,
      ProfileType: this.profileService.getProfileType(),
    };

    this.trackEvent('Profile Viewed', trackProperties);
  }

  private trackEvent(
    action: string,
    properties?: { [name: string]: any }
  ): void {
    let data: TrackingEventArgs = { action };
    // add properties, if defined
    if (properties) {
      data = {
        ...data,
        properties,
      };
    }
    // track event data
    this.tracker.trackEventData(data);
  }
}
